import type {
  HeadlessFlatTreeItemProps,
  TreeItemValue,
  TreeSelectionValue,
} from '@fluentui/react-components'
import type { SelectedSection } from '../../contexts/SelectedFilesContext'
import type { DocumentSectionTree, SectionType } from '../../types'

export type FlatTreeListItem = HeadlessFlatTreeItemProps & { title: string; type?: SectionType }

/**
 * Tree selection utilities for calculating minimal selection representation.
 * "Minimal" means: store only the top-most selected node when all its children are selected,
 * rather than listing every individual descendant (e.g. store "Chapter 1" not "§1.1, §1.2").
 *
 * Tree item values map directly to DocumentSectionTree.id (or fileId for the root).
 * This avoids positional indexing and simplifies context <-> tree state synchronisation.
 */

export interface FlattenedNode {
  value: string
  parentValue: string
  title: string
  type?: SectionType // undefined for the root (file) node
  children: string[] // values of direct children only
  allDescendants: string[] // values of ALL descendants (all levels)
}

function buildTreeMetadata(
  treeData: DocumentSectionTree[],
  parentId: string
): Map<string, FlattenedNode> {
  const metadata = new Map<string, FlattenedNode>()

  treeData.forEach((node) => {
    const directChildIds: string[] = []
    const allDescendants: string[] = []

    if (node.children?.length) {
      const childMetadata = buildTreeMetadata(node.children, node.id)

      childMetadata.forEach((childNode, childId) => {
        metadata.set(childId, childNode)
      })

      node.children.forEach((child) => {
        directChildIds.push(child.id)
        allDescendants.push(child.id)
        const childNode = childMetadata.get(child.id)
        if (childNode) allDescendants.push(...childNode.allDescendants)
      })
    }

    metadata.set(node.id, {
      value: node.id,
      parentValue: parentId,
      title: node.title,
      type: node.type,
      children: directChildIds,
      allDescendants,
    })
  })

  return metadata
}

/**
 * Flatten a DocumentSectionTree into FlatTree items using the actual node.id as value.
 * This ensures tree item values match context section IDs directly.
 */
export function flattenTreeStructure(
  treeData: DocumentSectionTree[],
  parentId: string
): FlatTreeListItem[] {
  const items: FlatTreeListItem[] = []

  treeData.forEach((node) => {
    items.push({
      value: node.id,
      parentValue: parentId,
      title: node.title,
      type: node.type,
    })

    if (node.children?.length) {
      items.push(...flattenTreeStructure(node.children, node.id))
    }
  })

  return items
}

/**
 * Build metadata for the full tree including the root (file) node.
 * The root node's value is fileId; its parentValue is '' (no parent).
 */
export function buildTreeMetadataWithRoot(
  treeData: DocumentSectionTree[],
  fileId: string,
  fileName: string
): Map<string, FlattenedNode> {
  const metadata = buildTreeMetadata(treeData, fileId)

  const allDescendants: string[] = []
  metadata.forEach((_, id) => allDescendants.push(id))

  metadata.set(fileId, {
    value: fileId,
    parentValue: '',
    title: fileName,
    type: undefined,
    children: treeData.map((n) => n.id),
    allDescendants,
  })

  return metadata
}

function areAllDescendantsSelected(
  nodeValue: string,
  metadata: Map<string, FlattenedNode>,
  selectedValues: Set<string>
): boolean {
  const node = metadata.get(nodeValue)
  if (!node) return false
  if (node.allDescendants.length === 0) return selectedValues.has(nodeValue)
  return node.allDescendants.every((d) => selectedValues.has(d))
}

/**
 * Fluent may mark a branch true when all visible children are checked in a filtered view.
 * Keep only nodes that are leaves, or branches whose full descendant set is selected.
 */
export function resolveSelectedTrueIds(
  selectedTrueIds: Set<string>,
  metadata: Map<string, FlattenedNode>
): Set<string> {
  const resolved = new Set<string>()

  selectedTrueIds.forEach((value) => {
    const node = metadata.get(value)
    if (!node) return

    const isLeaf = node.allDescendants.length === 0
    const isFullySelectedBranch = areAllDescendantsSelected(value, metadata, selectedTrueIds)

    if (isLeaf || isFullySelectedBranch) {
      resolved.add(value)
    }
  })

  return resolved
}

/**
 * Collapse a flat set of checked IDs to top-most nodes:
 * if every descendant of a parent is checked, replace them all with just the parent.
 */
function getMinimalSelection(
  selectedValues: Set<string>,
  metadata: Map<string, FlattenedNode>
): Set<string> {
  const minimal = new Set<string>()
  const processed = new Set<string>()

  function topmostSelected(value: string): string {
    const node = metadata.get(value)
    if (!node) return value
    let current = value
    let parentId = node.parentValue
    while (parentId) {
      const parent = metadata.get(parentId)
      if (!parent || !areAllDescendantsSelected(parentId, metadata, selectedValues)) break
      current = parentId
      parentId = parent.parentValue
    }
    return current
  }

  selectedValues.forEach((value) => {
    if (processed.has(value)) return
    const topmost = topmostSelected(value)
    minimal.add(topmost)
    processed.add(topmost)
    metadata.get(topmost)?.allDescendants.forEach((d) => processed.add(d))
  })

  return minimal
}

/** Expand a set of top-most node IDs to the full set of all their descendants. */
function expandSelection(
  minimalValues: Set<string>,
  metadata: Map<string, FlattenedNode>
): Set<string> {
  const expanded = new Set<string>()
  minimalValues.forEach((value) => {
    expanded.add(value)
    metadata.get(value)?.allDescendants.forEach((d) => expanded.add(d))
  })
  return expanded
}

function getFullyCheckedIds(
  selectedSections: SelectedSection[],
  metadata: Map<string, FlattenedNode>,
  fileId: string
): Set<string> {
  const rootNode = metadata.get(fileId)
  if (selectedSections.length === 0) {
    return new Set([fileId, ...(rootNode?.allDescendants ?? [])])
  }
  return expandSelection(new Set(selectedSections.map((s) => s.id)), metadata)
}

/** Collect node ids rendered in a filtered display tree (includes file root). */
export function collectVisibleNodeIds(
  filteredTree: DocumentSectionTree[],
  fileId: string
): Set<string> {
  const visibleIds = new Set<string>([fileId])
  flattenTreeStructure(filteredTree, fileId).forEach((item) => {
    visibleIds.add(String(item.value))
  })
  return visibleIds
}

function getVisibleDescendantIds(
  nodeId: string,
  metadata: Map<string, FlattenedNode>,
  visibleIds: Set<string>
): string[] {
  const node = metadata.get(nodeId)
  if (!node) return visibleIds.has(nodeId) ? [nodeId] : []

  return [nodeId, ...node.allDescendants].filter((id) => visibleIds.has(id))
}

/**
 * Derive Fluent checkedItems for a filtered view. Hidden selections stay in context
 * but only visible nodes receive checked/mixed states.
 */
export function buildInitialCheckedItemsForView(
  selectedSections: SelectedSection[],
  metadata: Map<string, FlattenedNode>,
  visibleIds: Set<string>,
  fileId: string
): Array<[string, boolean | 'mixed']> {
  const fullyChecked = getFullyCheckedIds(selectedSections, metadata, fileId)
  const result: Array<[string, boolean | 'mixed']> = []

  visibleIds.forEach((id) => {
    const node = metadata.get(id)
    if (!node) return

    if (fullyChecked.has(id)) {
      result.push([id, true])
      return
    }

    const hasCheckedVisibleDescendant = node.allDescendants.some(
      (descendantId) => visibleIds.has(descendantId) && fullyChecked.has(descendantId)
    )
    if (hasCheckedVisibleDescendant) {
      result.push([id, 'mixed'])
    }
  })

  return result
}

/** Toggle selection for a node and its visible descendants; preserve hidden selections. */
export function applyVisibleSelectionToggle(
  selectedTrueIds: Set<string>,
  toggledId: string,
  isChecked: boolean,
  metadata: Map<string, FlattenedNode>,
  visibleIds: Set<string>
): Set<string> {
  const next = new Set(selectedTrueIds)
  const affectedIds = getVisibleDescendantIds(toggledId, metadata, visibleIds)

  affectedIds.forEach((id) => {
    if (isChecked) {
      next.add(id)
    } else {
      next.delete(id)
    }
  })

  return next
}

export function selectedTrueIdsFromCheckedItems(
  checkedItems: Map<TreeItemValue, TreeSelectionValue>
): Set<string> {
  const selected = new Set<string>()
  checkedItems.forEach((value, key) => {
    if (value === true) selected.add(String(key))
  })
  return selected
}

/** Expand context selection to every checked node id in the full tree. */
export function getSelectedTrueIdsFromSections(
  selectedSections: SelectedSection[],
  metadata: Map<string, FlattenedNode>,
  fileId: string
): Set<string> {
  return selectedTrueIdsFromCheckedItems(
    new Map(
      buildInitialCheckedItems(selectedSections, metadata, fileId).filter(
        (entry): entry is [string, true] => entry[1] === true
      )
    )
  )
}

export function checkedItemsMapFromSelectedTrueIds(
  selectedTrueIds: Set<string>,
  metadata: Map<string, FlattenedNode>,
  visibleIds: Set<string>
): Map<string, TreeSelectionValue> {
  const map = new Map<string, TreeSelectionValue>()

  visibleIds.forEach((id) => {
    const node = metadata.get(id)
    if (!node) return

    if (selectedTrueIds.has(id)) {
      map.set(id, true)
      return
    }

    const hasSelectedVisibleDescendant = node.allDescendants.some(
      (descendantId) => visibleIds.has(descendantId) && selectedTrueIds.has(descendantId)
    )
    if (hasSelectedVisibleDescendant) {
      map.set(id, 'mixed')
    }
  })

  return map
}

/**
 * Build the initial checkedItems array for useHeadlessFlatTree_unstable from a context selection.
 * - [] (entire file selected) → root + all descendants marked true
 * - [...] (partial) → expand the minimal set, mark ancestors mixed
 */
export function buildInitialCheckedItems(
  selectedSections: SelectedSection[],
  metadata: Map<string, FlattenedNode>,
  fileId: string
): Array<[string, boolean | 'mixed']> {
  const result: Array<[string, boolean | 'mixed']> = []

  const fullyChecked: Set<string> = getFullyCheckedIds(selectedSections, metadata, fileId)

  metadata.forEach((node, id) => {
    if (fullyChecked.has(id)) {
      result.push([id, true])
    } else if (node.allDescendants.some((d) => fullyChecked.has(d))) {
      result.push([id, 'mixed'])
    }
  })

  return result
}

/**
 * Convert Fluent UI's checkedItems map (from onCheckedChange) into the minimal
 * SelectedSection[] representation stored in context.
 *
 * Returns:
 * - null           → nothing selected; remove file from context
 * - []             → entire file selected (root node is true)
 * - SelectedSection[] → partial selection; only top-most selected sections
 */
export function computeSelectedSectionsFromChecked(
  checkedItems: Map<TreeItemValue, TreeSelectionValue>,
  metadata: Map<string, FlattenedNode>,
  fileId: string
): SelectedSection[] | null {
  const rawSelected = selectedTrueIdsFromCheckedItems(checkedItems)

  if (rawSelected.size === 0) return null

  const selectedValues = resolveSelectedTrueIds(rawSelected, metadata)

  if (selectedValues.size === 0) return null

  const minimal = getMinimalSelection(selectedValues, metadata)

  if (minimal.has(fileId)) return []

  const sections: SelectedSection[] = []
  minimal.forEach((value) => {
    const node = metadata.get(value)
    if (node?.type) sections.push({ id: value, title: node.title, type: node.type })
  })

  return sections.length > 0 ? sections : null
}
